import React, { useState } from 'react';
import { Table, TimePicker, message, Space, Popconfirm, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { createOrUpdate, recalcularPorFecha } from '../../../../apis/ChecadaDiaApi';

const FormBodyDay = ({ items, setItems, columns, fecha }) => {
    const [editingKey, setEditingKey] = useState('');
    const [recalculando, setRecalculando] = useState(false);

    const isEditing = (record) => record.key === editingKey;

    const handleFieldChange = async (key, field, value) => {
        const newData = [...items];
        const index = newData.findIndex((item) => key === item.key);

        if (index > -1) {
            const item = newData[index];
            const updatedItem = {
                ...item,
                [field]: value,
            };

            newData.splice(index, 1, updatedItem);
            setItems(newData);

            const tieneEntrada = updatedItem.entrada && updatedItem.entrada !== '';
            const tieneSalida = updatedItem.salida && updatedItem.salida !== '';

            if (tieneEntrada && tieneSalida) {
                await createOrUpdateRecord(updatedItem);
            }
        }
    };

    const handleRecalcular = async () => {
        setRecalculando(true);
        try {
            const fechaFormato = dayjs(fecha).format('YYYY-MM-DD');
            const response = await recalcularPorFecha(fechaFormato);

            if (response.ok) {
                message.success(response.message || 'Checadas recalculadas exitosamente');

                // Recargar los datos actualizados
                window.location.reload();
            } else {
                message.error('Error al recalcular checadas');
            }
        } catch (error) {
            console.error('Error al recalcular:', error);
            message.error('Error al recalcular las checadas');
        } finally {
            setRecalculando(false);
        }
    };

    const createOrUpdateRecord = async (item) => {
        try {
            const fechaBase = dayjs(item.fecha).format('YYYY-MM-DD');

            const payload = {
                empleado_id: item.empleado_id,
                fecha: fechaBase,
            };

            if (item.id) {
                payload.id = item.id;
            }

            if (item.entrada) {
                payload.entrada = `${fechaBase}T${item.entrada}:00`;
            }

            if (item.salida) {
                payload.salida = `${fechaBase}T${item.salida}:00`;
            }

            if (item.estacion_id) {
                payload.estacion_id = item.estacion_id;
            }

            const response = await createOrUpdate(payload);

            if (response.ok) {
                const updatedData = response.payload;
                const newData = [...items];
                const index = newData.findIndex((i) => i.key === item.key);

                if (index > -1) {
                    newData[index] = {
                        ...newData[index],
                        id: updatedData.id,
                        entrada: updatedData.entrada ? dayjs(updatedData.entrada).format('HH:mm') : '',
                        salida: updatedData.salida ? dayjs(updatedData.salida).format('HH:mm') : '',
                        horas_trabajadas: updatedData.horas_trabajadas || 0,
                        horas_extra: updatedData.horas_extra || 0,
                        horas_autorizadas: updatedData.horas_autorizadas || 0,
                        descanso: updatedData.descanso || 0,
                        festivo: updatedData.festivo || 0,
                        estacion_id: updatedData.estacion_id,
                        horario_id: updatedData.horario_id,
                        referencia: updatedData.referencia || '',
                        tipo_dia_texto: updatedData.tipo_dia_texto || newData[index].tipo_dia_texto,
                        tipo_dia: updatedData.tipo_dia || newData[index].tipo_dia,
                        retardo: updatedData.retardo || 0,
                        asistencia: updatedData.asistencia || 0,
                    };
                    setItems(newData);
                }

                message.success('Registro guardado y calculado automáticamente');
            }

        } catch (error) {
            console.error('Error al guardar registro:', error);
            message.error('Error al guardar el registro');
        }
    };

    const editableColumns = columns.map((col) => {
        if (['entrada', 'salida'].includes(col.dataIndex)) {
            return {
                ...col,
                onCell: (record) => ({
                    record,
                    dataIndex: col.dataIndex,
                    title: col.title,
                    editing: true,
                    handleFieldChange,
                }),
            };
        }
        return col;
    });

    // Componente de celda editable
    const EditableCell = ({
        editing,
        dataIndex,
        title,
        record,
        children,
        handleFieldChange,
        ...restProps
    }) => {
        const renderInput = () => {
            switch (dataIndex) {
                case 'entrada':
                case 'salida':
                    return (
                        <TimePicker
                            value={record[dataIndex] ? dayjs(record[dataIndex], 'HH:mm') : null}
                            onChange={(time) => {
                                const timeStr = time ? time.format('HH:mm') : '';
                                handleFieldChange(record.key, dataIndex, timeStr);
                            }}
                            format="HH:mm"
                            placeholder=" "
                            inputReadOnly
                            open={false}
                            allowClear={false}
                            style={{ width: '100%' }}
                        />
                    );

                default:
                    return children;
            }
        };

        return (
            <td {...restProps}>
                {editing ? renderInput() : children}
            </td>
        );
    };

    const components = {
        body: {
            cell: EditableCell,
        },
    };

    return (
        <>
            <Table
                components={components}
                dataSource={items}
                columns={editableColumns}
                pagination={{
                    pageSize: 50,
                    showSizeChanger: true,
                    pageSizeOptions: ['20', '50', '100', '200'],
                    showTotal: (total) => `Total ${total} empleados`,
                }}
                scroll={{ x: 'max-content' }}
                bordered
                size="small"
                rowClassName={() => 'table-row-white'}
            />

            <Space style={{ marginTop: 16, justifyContent: 'center', width: '100%', display: 'flex' }}>
                <Popconfirm
                    title="Actualización de Checadas"
                    description="¿Estás seguro de recalcular todas las checadas de este día?"
                    onConfirm={handleRecalcular}
                    okText="Sí, recalcular"
                    cancelText="Cancelar"
                >
                    <Button
                        type="primary"
                        icon={<ReloadOutlined />}
                        loading={recalculando}
                        size="large"
                    >
                        Actualización de Checadas
                    </Button>
                </Popconfirm>
            </Space>
        </>
    );
};

export default FormBodyDay;